import React, { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import {
  Container,
  Row,
  Card,
  Form,
  Col,
  Input,
  CardHeader,
  CardBody
} from 'reactstrap';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import ReactLoader from 'components/Loader';
import ReactButton from 'components/Buttons/ReactButton';
import FlexSelect from 'components/Inputs/FlexSelect';
import QueueService from 'views/Queue/service';
import {
  templateParamsRegex,
  broadcastTypeOfClosingConversationOptions,
  broadcastTypeOfSmsModelOptions
} from '../utils';
import TemplateMessageFilling from './components/TemplateMessageFillingList';

import BroadcastListSMSService from '../service';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { customFieldTypes } from 'views/fieldConfigs/utils/customFieldTypes';
import { ScreenPrompt } from 'components/ScreenPrompt';
import { DatesHelper } from 'utils/classes/DatesHelper';
import { FlexController } from 'components/FlexControllerInput';

const hourRules = {
  required: 'Hora é obrigatório!',
  pattern: {
    value: /^[0-2][0-9]:[0-5][0-9]:[0-5][0-9]$/,
    message: 'Hora inválida! (00:00:00)'
  }
};

export const ChatBroadcastListSMSForm = (props) => {
  const { id } = props.match.params;
  const history = useHistory();
  const { reset, control, handleSubmit, errors, setValue, formState, watch } =
    useForm();
  const { isDirty } = formState;

  const [loading, setLoading] = useState(false);
  const [queues, setQueues] = useState(null);
  const [templateMessageText, setTemplateMessageText] = useState('');
  const [templateParams, setTemplateParams] = useState({});
  const [isUpdate, setIsUpdate] = useState(false);
  const isScheduled = watch("isScheduled") || false
  const [clientFields, setClientFields] = useState([]);

  const defaultContactRecordType = 301;
  const { customFields, getCustomFields, isLoadingCustomFields } =
    useEntityCustomFieldsByRecordType(
      entitiesName.CONTACT,
      defaultContactRecordType
    );

  const getQueues = useCallback(async () => {
    QueueService.getAll()
      .then((response) => {
        const queuesData = response.data.data;
        if (queuesData) {
          const queuesDTO = queuesData.map(({ id, name }) => ({ id, name }));
          setQueues(queuesDTO);
        }
      })
      .catch(() => {
        toast('Ocorreu um erro ao carregar as filas');
      });
  }, []);

  const getClientFields = useCallback(() => {
    if (isLoadingCustomFields) return;
    const defaultFields = [
      { label: 'Nome', value: 'nome' },
      { label: 'Telefone', value: 'phone' }
    ];

    const customizedFields = customFields
      .filter((field) => field.type === customFieldTypes.TEXT)
      .map((field) => ({
        label: field.label,
        value: field.name
      }));

    setClientFields(defaultFields.concat(customizedFields || []));
  }, [customFields, isLoadingCustomFields]);

  const getBroadcastListToEdit = useCallback(
    (broadcastListId) => {
      setLoading(true);

      BroadcastListSMSService.get(broadcastListId)
        .then((response) => {
          const broadcastListData = response.data;

          reset({
            ...broadcastListData,
            title: broadcastListData.title,
            queueId: broadcastListData.queue_id,
            broadcastTypeOfClosingConversation:
              broadcastListData.broadcastTypeOfClosingConversation,
            closingMessage: broadcastListData.closingMessage ?? '',
            broadcastTypeOfSmsModelOptions:
              broadcastListData.broadcastTypeOfSmsModelOptions,
            scheduledDate: DatesHelper.convertDateObjectToBrazilianFormat(new Date(broadcastListData.scheduledDatetime)),
            scheduledTime: DatesHelper.applyHours(broadcastListData.scheduledDatetime.split("T")[1].slice(0, 7), -3),
          });
          setTemplateMessageText(broadcastListData.message);

          const params = broadcastListData.message.match(templateParamsRegex);
          const currentParams = {};
          if (params) {
            params.forEach((value, index) => {
              currentParams[`${value}`] = broadcastListData.params[index];
            });
          }
          setTemplateParams(currentParams);
          setIsUpdate(true);
        })
        .catch((err) => {
          const msg =
            err.response && err.response.data && err.response.data.message;
          toast.error(msg || 'Erro ao buscar campanha', {
            autoClose: 3000,
            closeOnClick: true
          });
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [reset]
  );

  useEffect(() => {
    getQueues();
  }, [getQueues]);

  useEffect(() => {
    getCustomFields();
  }, [getCustomFields]);

  useEffect(() => {
    getClientFields();
  }, [getClientFields]);

  useEffect(() => {
    if (id) getBroadcastListToEdit(id);
  }, [id, getBroadcastListToEdit]);

  const onSubmit = async (data) => {
    const extractParams = (message = '') => {
      return message.match(templateParamsRegex);
    };

    const params = extractParams(templateMessageText);

    const keys =
      isUpdate && params ? params.sort() : Object.keys(templateParams).sort();
    const newTemplateParams = keys.map((number) => templateParams[number]);

    data.message = templateMessageText;
    data.templateParams = newTemplateParams;
    data.templateType = 'TEXT';
    data.sendingScheduleDate = data.scheduledDate || DatesHelper.getTodayInBrazilianFormat();
    data.sendingScheduleTime = data.scheduledTime || "00:00:00";
    data.scheduledDatetime = DatesHelper.compileDateAndTime(data.sendingScheduleDate, data.sendingScheduleTime);
    data.user = { name: localStorage.getItem("name"), login: localStorage.getItem("login") };

    const requestBodyDTO = { ...data };
    try {
      isUpdate
        ? await BroadcastListSMSService.update(id, requestBodyDTO)
        : await BroadcastListSMSService.create(requestBodyDTO);
      toast.success(
        `Campanha Ativa SMS ${isUpdate ? 'atualizada' : 'criada'} com sucesso`
      );
      history.push('/admin/chat/broadcast-list-sms');
    } catch (error) {
      const errorMessage =
        error &&
        error.response &&
        error.response.data &&
        error.response.data.message;

      toast.error(errorMessage || 'Erro ao criar campanha');
    }

    return;
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  const cancelAction = () => {
    history.push('/admin/chat/broadcast-list-sms');
  };

  const changeText = (event) => {
    setTemplateMessageText(event.target.value);
  };

  return (
    <>
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <SimpleHeader
        name="Form validation"
        parentName="Forms"
        returnPath="/admin/chat/broadcast-list-sms"
        buttonTitle="Voltar para a pagina de campanhas"
      />
      <Container className="mt--6" fluid>
        <Row className="mb-3">
          <div className="col">
            <div className="card-wrapper">
              <Card>
                <CardHeader>
                  <h3 className="mb-0">Campanha Ativa SMS</h3>
                </CardHeader>
                <CardBody>
                  {loading ? (
                    <Row className="justify-content-md-center">
                      <ReactLoader />
                    </Row>
                  ) : (
                    <Form
                      className="needs-validation"
                      onSubmit={handleSubmit(onSubmit)}
                    >
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">Título *</label>
                          <Controller
                            as={Input}
                            control={control}
                            name="title"
                            rules={{ required: 'O título é obrigatório!' }}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="title"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <label className="form-control-label">Fila *</label>
                          <Controller
                            render={(props) => (
                              <FlexSelect
                                isClearable={true}
                                dataOptions={queues || []}
                                isMulti={false}
                                closeMenuOnSelect={true}
                                value={props.value}
                                valueController={setValue}
                                fieldName="queueId"
                                labelName="name"
                                valueName="id"
                              />
                            )}
                            control={control}
                            name="queueId"
                            rules={{
                              required: 'Selecione uma fila'
                            }}
                            defaultValue=""
                          />
                          <ErrorMessage
                            errors={errors}
                            name="queueId"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md="6">
                          <label className="form-control-label">Texto *</label>
                          <Controller
                            render={() => (
                              <Input
                                type="textarea"
                                rows="5"
                                value={templateMessageText}
                                onChange={changeText}
                                name="message"
                                id="message"
                              />
                            )}
                            control={control}
                            name="message"
                            defaultValue={templateMessageText}
                          />
                          <ErrorMessage
                            errors={errors}
                            name="message"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                        <Col md="6">
                          <div>
                            <label className="form-control-label">
                              Tipo de conversa *
                            </label>
                            <Controller
                              render={(props) => (
                                <FlexSelect
                                  isClearable={true}
                                  dataOptions={
                                    broadcastTypeOfClosingConversationOptions
                                  }
                                  isMulti={false}
                                  closeMenuOnSelect={true}
                                  value={props.value}
                                  valueController={setValue}
                                  fieldName="broadcastTypeOfClosingConversation"
                                  labelName="label"
                                  valueName="value"
                                />
                              )}
                              rules={{
                                required: 'Selecione um tipo de abertura'
                              }}
                              control={control}
                              name="broadcastTypeOfClosingConversation"
                              defaultValue=""
                            />
                            <ErrorMessage
                              errors={errors}
                              name="broadcastTypeOfClosingConversation"
                              render={({ message }) =>
                                errorFormMessage(message)
                              }
                            />
                          </div>

                          <div>
                            <label className="form-control-label">
                              Tipo de modelo de SMS *
                            </label>
                            <Controller
                              render={(props) => (
                                <FlexSelect
                                  isClearable={true}
                                  dataOptions={broadcastTypeOfSmsModelOptions}
                                  isMulti={false}
                                  closeMenuOnSelect={true}
                                  value={props.value}
                                  valueController={setValue}
                                  fieldName="broadcastTypeOfSmsModelOptions"
                                  labelName="label"
                                  valueName="value"
                                />
                              )}
                              rules={{
                                required: 'Selecione um tipo de abertura'
                              }}
                              control={control}
                              name="broadcastTypeOfSmsModelOptions"
                              defaultValue={1}
                            />
                            <ErrorMessage
                              errors={errors}
                              name="broadcastTypeOfSmsModelOptions"
                              render={({ message }) =>
                                errorFormMessage(message)
                              }
                            />
                          </div>

                        </Col>
                      </Row>
                      <Row className='mb-3'>
                        <Col>
                          <Row className="mb-3">
                            <Col sm={12} className="d-flex align-items-center">
                              <FlexController
                                type="checkbox"
                                control={control}
                                checked={isScheduled}
                                name="isScheduled"
                                valueController={setValue}
                                textoDoLabel="Fazer disparo em horário e data programados (agendar disparo)?"
                              />
                            </Col>
                          </Row>
                          {isScheduled ? (
                            <Row className="mb-3">
                              <Col md="3">
                                <FlexController
                                  type="date"
                                  control={control}
                                  errors={errors}
                                  name="scheduledDate"
                                  textoDoLabel="Data do disparo*"
                                  valueController={setValue}
                                  defaultValue={DatesHelper.getTodayInBrazilianFormat()}
                                />
                              </Col>
                              <Col md="3">
                                <FlexController
                                  type="time"
                                  errors={errors}
                                  control={control}
                                  name="scheduledTime"
                                  rules={hourRules}
                                  valueController={setValue}
                                  defaultValue="00:00:00"
                                  textoDoLabel="Horário do disparo*"
                                />
                              </Col>
                            </Row>
                          ) : null}
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={12}>
                          {!!clientFields &&
                            !!clientFields.length &&
                            !loading ? (
                            <TemplateMessageFilling
                              templateMessage={templateMessageText}
                              templateParams={
                                Object.keys(templateParams).length > 0
                                  ? templateParams
                                  : {}
                              }
                              setTemplateParams={setTemplateParams}
                              clientFields={clientFields}
                              isUpdate={isUpdate}
                              templateType="TEXT"
                            />
                          ) : loading ? (
                            <Col style={{ marginTop: '2rem' }}>
                              <ReactLoader height={60} />
                            </Col>
                          ) : null}
                        </Col>
                        <Col>
                          <ErrorMessage
                            errors={errors}
                            name="templateParams"
                            render={({ message }) => errorFormMessage(message)}
                          />
                        </Col>
                      </Row>
                      <hr />
                      <ReactButton btnColor="confirmation" type="submit">
                        Salvar
                      </ReactButton>

                      <ReactButton
                        btnColor="cancelation"
                        onClick={() => cancelAction()}
                      >
                        Cancelar
                      </ReactButton>
                    </Form>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </Row>
      </Container>
    </>
  );
};
